import React from 'react';
import LegalPage from '../components/LegalPage';

// Source: WorldVibe Community Guidelines. Edit verbatim if updated.
const CONTENT = `
**Meet the World in Real Time**

Welcome to WorldVibe.

WorldVibe is a global social platform where people connect, build friendships, join communities, play games, and share experiences across cultures and borders.

These Community Guidelines help ensure that WorldVibe remains safe, respectful, and enjoyable for everyone.

By using WorldVibe, you agree to follow these guidelines.

## Our Mission

WorldVibe exists to bring people together through meaningful conversations, shared interests, and positive social experiences.

We encourage:
- Respect
- Inclusion
- Curiosity
- Creativity
- Community Building
- Healthy Interaction

## Respect Other Users

Treat others the way you would like to be treated.

**Allowed:**
- Friendly conversations
- Constructive disagreement
- Humor and lighthearted interactions
- Cultural exchange

**Not Allowed:**
- Harassment
- Bullying
- Intimidation
- Personal attacks
- Threatening behavior

Repeated violations may result in account restrictions or permanent removal.

## No Hate Speech

WorldVibe welcomes people from all backgrounds.

Content targeting individuals or groups based on protected characteristics is not permitted.

Examples include:
- Racist content
- Religious hatred
- Ethnic slurs
- Discriminatory language
- Calls for violence against groups

Such violations may result in immediate suspension.

## Keep Conversations Safe

Users must not:
- Encourage self-harm
- Promote dangerous activities
- Share instructions for illegal conduct
- Threaten violence
- Promote terrorism or extremist activities

User safety is a top priority.

## No Fraud or Scams

WorldVibe is not a marketplace for deception.

Do not:
- Impersonate other people
- Request passwords or OTPs
- Conduct financial scams
- Promote fake giveaways
- Use phishing techniques
- Attempt to steal personal information

Accounts engaged in fraudulent activity will be permanently removed.

## Protect Privacy

Respect the privacy of others.

Do not share:
- Personal addresses
- Phone numbers
- Financial information
- Government identification
- Private conversations without consent
- Sensitive personal information

Always obtain permission before sharing information about another user.

## Dating and Relationships

WorldVibe encourages respectful connections.

**Users may:**
- Meet new people
- Form friendships
- Participate in dating features
- Celebrate relationships

**Users may not:**
- Harass others for attention
- Send repeated unwanted messages
- Pressure users into sharing personal information
- Use manipulation or coercion

Consent matters at all times.

## Planets and Community Management

Planet Creators and Guardians help shape community experiences.

**Planet leaders should:**
- Encourage respectful discussions
- Enforce community rules fairly
- Welcome new members
- Report harmful behavior

**Planet leaders may not:**
- Abuse moderation powers
- Target users unfairly
- Create communities that promote harmful content

## Gifts, Stars and Economy

WorldVibe's economy is designed for fun and recognition.

**Users may:**
- Send gifts
- Earn Stars
- Participate in events

**Users may not:**
- Sell accounts
- Manipulate rankings
- Exploit bugs
- Use automated systems to gain rewards
- Purchase or sell XP, ranks, or account privileges outside approved systems

Cosmic Ranks must be earned through participation.

## Games and Fair Play

WorldVibe games should remain fun and fair.

Do not:
- Cheat
- Use bots
- Exploit game mechanics
- Manipulate leaderboards

Violations may result in loss of rewards and account restrictions.

## Content Standards

Users are responsible for content they share.

Do not post:
- Illegal content
- Graphic violence
- Explicit sexual content
- Child exploitation material
- Copyright-infringing content
- Malicious software or harmful links

Content violating these rules will be removed.

## Spam and Unwanted Activity

Do not:
- Flood chats
- Send repetitive messages
- Mass-message users
- Create multiple accounts to manipulate engagement
- Promote unrelated products or services without permission

WorldVibe is designed for authentic interaction.

## Reporting and Enforcement

If you encounter behavior that violates these guidelines:
1. Open the user's profile.
2. Select Report.
3. Choose a reason.
4. Submit the report.

Our moderation team reviews every report within **24 hours** and acts immediately on anything that objectifies real people, is mean-spirited, or contains hate, harassment, nudity, or child safety concerns. Automated moderation additionally scans every message, photo, and profile at post time — critical categories (CSAE, sexual content involving minors) are escalated to our child-safety team in real time.

### Zero tolerance

WorldVibe has **zero tolerance** for:
- Content that objectifies, ranks, or degrades real people
- Nudity, sexual, or otherwise explicit content
- Hate speech, harassment, threats, or bullying
- Impersonation or misleading identities
- Any content involving minors in an inappropriate context

Abusive accounts are terminated on first substantiated report. You can also **block** any user at any time from their profile menu — blocked users cannot see or contact you.

### Possible Enforcement Actions

Depending on the severity of a violation, WorldVibe may:
- Remove content
- Issue warnings
- Restrict features
- Suspend accounts
- Permanently ban accounts

Serious violations may result in immediate account termination.

## Building the WorldVibe Universe

Every Explorer, Traveler, Astronaut, and Supreme Being contributes to the WorldVibe community.

Help us create a universe that is welcoming, respectful, and enjoyable for everyone.

## Need Help?

- **Support**: support@worldvibe.online
- **Security Issues**: security@worldvibe.online
- **Business Inquiries**: hello@worldvibe.online
- **Executive Office**: ceo@worldvibe.online
`;

export default function Guidelines() {
  return (
    <LegalPage
      title="Community Guidelines"
      tagline="How we keep WorldVibe safe, kind, and fun"
      accent="#34D399"
      content={CONTENT}
      backTo="/"
      testId="guidelines-page"
    />
  );
}
